// Generated by ReScript, PLEASE EDIT WITH CARE

import * as I18n from "./I18n.res.js";
import * as React from "react";
import * as Touchable from "./Touchable.res.js";
import * as Stdlib_Array from "@rescript/runtime/lib/es6/Stdlib_Array.js";
import * as Stdlib_Option from "@rescript/runtime/lib/es6/Stdlib_Option.js";
import * as ReactDomElement from "./ReactDomElement.res.js";
import * as Primitive_option from "@rescript/runtime/lib/es6/Primitive_option.js";
import * as JsxRuntime from "react/jsx-runtime";

function extensionFromType(_type) {
  if (_type === "spreadsheetml") {
    return ".xlsx";
  } else if (_type === "spreadsheetopen") {
    return ".ods";
  } else if (_type === "excel") {
    return ".xls";
  } else if (_type === "csv") {
    return ".csv";
  } else if (_type === "png") {
    return ".png";
  } else if (_type === "zip") {
    return ".zip";
  } else {
    return ".jpg";
  }
}

function mimeFromType(type) {
  if (type === "spreadsheetml") {
    return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  } else if (type === "spreadsheetopen") {
    return "application/vnd.oasis.opendocument.spreadsheet";
  } else if (type === "excel") {
    return "application/vnd.ms-excel";
  } else if (type === "csv") {
    return "text/csv";
  } else if (type === "png") {
    return "image/png";
  } else if (type === "zip") {
    return "application/zip";
  } else {
    return "image/jpeg";
  }
}

function typeFromMime(mime) {
  switch (mime) {
    case "application/vnd.ms-excel" :
      return {
        TAG: "Ok",
        _0: "excel"
      };
    case "application/vnd.oasis.opendocument.spreadsheet" :
      return {
        TAG: "Ok",
        _0: "spreadsheetopen"
      };
    case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" :
      return {
        TAG: "Ok",
        _0: "spreadsheetml"
      };
    case "application/x-zip-compressed" :
    case "application/zip" :
      return {
        TAG: "Ok",
        _0: "zip"
      };
    case "image/jpeg" :
      return {
        TAG: "Ok",
        _0: "jpeg"
      };
    case "image/png" :
      return {
        TAG: "Ok",
        _0: "png"
      };
    case "text/csv" :
      return {
        TAG: "Ok",
        _0: "csv"
      };
    default:
      return {
        TAG: "Error",
        _0: undefined
      };
  }
}

function messageFromError(type) {
  if (typeof type !== "object") {
    if (type === "FileNotFound") {
      return I18n.t("Couldn't import file.");
    } else {
      return I18n.t("The imported file is too heavy.");
    }
  }
  let fileTypes = type._0;
  if (fileTypes.length > 1) {
    return I18n.template(I18n.t("The imported file must be of the following types: {{types}}."), {
      types: Stdlib_Array.reduce(fileTypes, "", (acc, fileType) => acc + (
        acc.length > 0 ? ", " : ""
      ) + extensionFromType(fileType))
    }, undefined);
  } else {
    return I18n.template(I18n.t("The imported file must be of type {{type}}."), {
      type: extensionFromType(fileTypes[0])
    }, undefined);
  }
}

let extensionFromFilename = (filename => filename.split('.').pop());

function isFileBadType(file, types) {
  let fileType = typeFromMime(file.type);
  if (fileType.TAG !== "Ok") {
    return true;
  }
  let fileType$1 = fileType._0;
  return !types.some(current => current === fileType$1);
}

function isFileBadExtension(file, types) {
  if (file.type !== "") {
    return true;
  }
  let extension = extensionFromFilename(file.name);
  return types.every(fileType => extensionFromType(fileType) !== "." + extension);
}

function FilePicker(props) {
  let onError = props.onError;
  let onChange = props.onChange;
  let __disabled = props.disabled;
  let __maxSizeMb = props.maxSizeMb;
  let types = props.types;
  let maxSizeMb = __maxSizeMb !== undefined ? __maxSizeMb : 1;
  let disabled = __disabled !== undefined ? __disabled : false;
  let ref = React.useRef(null);
  let handlePicking = React.useCallback(event => {
    let files = event.currentTarget.files;
    let match = files[0];
    if (match === undefined) {
      return onError(I18n.t("Couldn't import file."));
    }
    let file = Primitive_option.valFromOption(match);
    if (types !== undefined && isFileBadType(file, types) && isFileBadExtension(file, types)) {
      return onError(messageFromError({
        TAG: "FileBadExtension",
        _0: types
      }));
    }
    if (file.size > maxSizeMb * 1048576) {
      return onError(I18n.t("The imported file is too heavy."));
    } else {
      return onChange(file);
    }
  }, [types]);
  let onPress = React.useCallback(param => Stdlib_Option.forEach(Stdlib_Option.flatMap(ReactDomElement.fromRef(ref), prim => prim), prim => {
    prim.click();
  }), [ref]);
  let accept = React.useMemo(() => {
    if (types !== undefined) {
      return Stdlib_Array.reduce(types, "", (acc, fileType) => acc + mimeFromType(fileType) + ", " + extensionFromType(fileType) + ", ");
    }
  }, [types]);
  return JsxRuntime.jsxs(JsxRuntime.Fragment, {
    children: [
      JsxRuntime.jsx("input", {
        ref: Primitive_option.some(ref),
        style: {
          display: "none"
        },
        accept: accept,
        type: "file",
        onChange: handlePicking
      }),
      JsxRuntime.jsx(Touchable.make, {
        children: props.children,
        disabled: disabled,
        onPress: onPress
      })
    ]
  });
}

let make = React.memo(FilePicker);

export {
  isFileBadType,
  isFileBadExtension,
  messageFromError,
  typeFromMime,
  extensionFromType,
  make,
}
/* make Not a pure module */
